import type { Account, BuyerRow, Message, PatchBuyerBody, PatchConversationBody, SendReplyResponse, Template } from "./types";

/**
 * Same-origin fetch wrapper. In dev vite proxies /api → :8787;
 * in prod the Pages site is fronted by a Worker route on the same
 * host, so no base URL needed either way.
 */
async function get<T>(path: string): Promise<T> {
  const resp = await fetch(path, { headers: { Accept: "application/json" } });
  if (!resp.ok) throw new Error(`GET ${path} → ${resp.status}`);
  return resp.json() as Promise<T>;
}

async function send<T>(
  method: "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const resp = await fetch(path, {
    method,
    headers: {
      "Accept":       "application/json",
      "Content-Type": "application/json; charset=utf-8",
      ...(extraHeaders ?? {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`${method} ${path} → ${resp.status} ${text}`);
  }
  return resp.json() as Promise<T>;
}

export const api = {
  listAccounts:       (): Promise<Account[]> => get<Account[]>("/api/accounts"),
  listBuyers:         (): Promise<BuyerRow[]> => get<BuyerRow[]>("/api/buyers"),
  listTemplates:      (): Promise<Template[]> => get<Template[]>("/api/templates"),
  patchConversation:  (id: string, body: PatchConversationBody): Promise<unknown> =>
    send("PATCH", `/api/conversations/${encodeURIComponent(id)}`, body),
  archiveConversation:    (id: string): Promise<unknown> =>
    send("PATCH", `/api/conversations/${encodeURIComponent(id)}`, { archived: true }),
  unarchiveConversation:  (id: string): Promise<unknown> =>
    send("PATCH", `/api/conversations/${encodeURIComponent(id)}`, { archived: false }),
  listMessages:       (convId: string): Promise<Message[]> =>
    get<Message[]>(`/api/conversations/${encodeURIComponent(convId)}/messages`),
  postReply:          (convId: string, text: string, idempotencyKey: string): Promise<SendReplyResponse> =>
    send<SendReplyResponse>(
      "POST",
      `/api/conversations/${encodeURIComponent(convId)}/reply`,
      { text },
      { "Idempotency-Key": idempotencyKey },
    ),
  markRead:           (convId: string): Promise<{ ok: boolean; marked: number }> =>
    send("POST", `/api/conversations/${encodeURIComponent(convId)}/mark-read`),
  markAllRead:        (): Promise<{ ok: boolean; marked: number }> =>
    send("POST", `/api/messages/mark-all-read`),
  patchBuyer:         (buyerId: string, body: PatchBuyerBody): Promise<unknown> =>
    send("PATCH", `/api/buyers/${encodeURIComponent(buyerId)}`, body),
};
